import { ReactElement, useState, useEffect } from 'react';
import { useGrievance } from '../../contexts/GrievanceContexts';
import { Users, FileText, AlertTriangle, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

interface DashboardStats {
  totalUsers: number;
  totalGrievances: number;
  pendingGrievances: number;
  resolvedGrievances: number;
  inProgressGrievances: number;
  todaySubmissions: number;
}

const AdminDashboard = (): ReactElement => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalGrievances: 0,
    pendingGrievances: 0,
    resolvedGrievances: 0,
    inProgressGrievances: 0,
    todaySubmissions: 0
  });

  const { 
    totalUsers,
    totalGrievances,
    pendingGrievances,
    resolvedGrievances,
    inProgressGrievances,
    todaySubmissions
  } = useGrievance();

  useEffect(() => {
    setStats({
      totalUsers,
      totalGrievances,
      pendingGrievances,
      resolvedGrievances,
      inProgressGrievances,
      todaySubmissions,
    });
  }, [totalUsers, totalGrievances, pendingGrievances, resolvedGrievances, inProgressGrievances, todaySubmissions]);

  // No-op; kept for potential future extensions

  const StatCard = ({ title, value, icon: Icon, color, bgColor }: {
    title: string;
    value: number;
    icon: any;
    color: string;
    bgColor: string;
  }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${bgColor}`}>
          <Icon className={`h-6 w-6 ${color === '#ef4444' ? 'text-red-600' : color === '#f59e0b' ? 'text-yellow-600' : color === '#10b981' ? 'text-green-600' : 'text-blue-600'}`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Super Admin Dashboard
        </h1>
        <p className="text-gray-600">
          Welcome to the Grievance Management System
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={Users}
          color="#3b82f6"
          bgColor="bg-blue-100"
        />
        <StatCard
          title="Total Grievances"
          value={stats.totalGrievances}
          icon={FileText}
          color="#8b5cf6"
          bgColor="bg-purple-100"
        />
        <StatCard
          title="Pending Review"
          value={stats.pendingGrievances}
          icon={AlertTriangle}
          color="#f59e0b"
          bgColor="bg-yellow-100"
        />
        <StatCard
          title="In Progress"
          value={stats.inProgressGrievances}
          icon={Clock}
          color="#3b82f6"
          bgColor="bg-blue-100"
        />
        <StatCard
          title="Resolved"
          value={stats.resolvedGrievances}
          icon={CheckCircle}
          color="#10b981"
          bgColor="bg-green-100"
        />
        <StatCard
          title="Today's Submissions"
          value={stats.todaySubmissions}
          icon={TrendingUp}
          color="#ef4444"
          bgColor="bg-red-100"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/super-admin/users"
              className="block w-full text-left px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
            >
              <div className="font-medium text-blue-900">Manage Users</div>
              <div className="text-sm text-blue-600">Add, edit, or remove system users</div>
            </Link>
            <button className="w-full text-left px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
              <div className="font-medium text-green-900">Review Grievances</div>
              <div className="text-sm text-green-600">Process pending grievances</div>
            </button>
            <button className="w-full text-left px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors">
              <div className="font-medium text-purple-900">System Settings</div>
              <div className="text-sm text-purple-600">Configure system parameters</div>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">New grievance submitted</div>
                <div className="text-xs text-gray-500">2 minutes ago</div>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">User registration approved</div>
                <div className="text-xs text-gray-500">15 minutes ago</div>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">Grievance status updated</div>
                <div className="text-xs text-gray-500">1 hour ago</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">Database: Online</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">API Server: Running</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">Email Service: Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
